import { useQuery } from '@tanstack/react-query';
import { LAB_VERDICT_LABELS, type LabItemData, type LabItemKind, type LabStatus, type LabVerdict } from '@raro/domain';
import { Button, MachineRaroBrand, Segmented } from '@raro/ui';
import type { ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

type Item = {
  id: string; kind: LabItemKind; status: string; title: string | null; body: string | null; data: LabItemData; author: string | null; round: number; pinned: boolean; wall: { x: number; y: number } | null; wallNote: string | null;
  image: { url: string; thumbUrl: string } | null; board: { brand: string; product: string; note: string; pageUrl: string; image: { url: string; thumbUrl: string } | null }[] | null; judgments: { userId: string; name: string; verdict: LabVerdict; note: string | null }[]; tally: { keep: number; maybe: number; drop: number; split: boolean };
};
type Round = { id: string; number: number; question: string; constraints: string | null; status: LabStatus; summary: string | null; items: Item[] };
type Room = { id: string; title: string; question: string | null; context: string | null; organization: { id: string; name: string }; project: { id: string; name: string } | null; rounds: Round[] };
type Kind = 'pedido' | 'prova' | 'wall' | 'prateleira';
const KIND: Record<Kind, { eyebrow: string; note: string }> = {
  pedido: { eyebrow: 'solicitação', note: 'o que se quer ver e o que não muda; referências como estão hoje' },
  prova: { eyebrow: 'prova de conceito', note: 'estudo gerado por IA: serve para decidir o caminho, não é arte final' },
  prateleira: { eyebrow: 'prateleira', note: 'embalagens tiradas das páginas de produto das lojas; a versão de cada uma não foi conferida. referência interna, não reproduzir' },
  wall: { eyebrow: 'wall da sala', note: 'o que a sala marcou como interessante; imagens geradas por IA são estudo, não arte final' },
};
const chunk = <T,>(list: T[], n: number) => Array.from({ length: Math.max(1, Math.ceil(list.length / n)) }, (_, i) => list.slice(i * n, i * n + n));

/**
 * As pranchas do LAB, sempre em A4 horizontal com o mesmo cabeçalho e rodapé (marca, cliente, projeto, data e hora, quem
 * pediu, a ressalva): **solicitação** (para quem vai produzir: a pergunta, o que não muda, as referências), **prova de
 * conceito** (para quem vai decidir: o que foi testado lado a lado, com o placar) e **wall** (a seleção da sala como foi
 * arranjada, com os comentários). "Para o cliente" tira os nomes e os porquês de cada pessoa e deixa o placar. O PDF sai
 * pela impressão do navegador.
 */
export function LabBoardPage() {
  const { id = '' } = useParams();
  const [sp, setSp] = useSearchParams();
  const { me } = useAuth();
  const q = useQuery({ queryKey: ['lab-room', id], queryFn: () => api.get<Room>(`/lab-rooms/${id}`) });
  const d = q.data;
  const kind: Kind = (['pedido', 'prova', 'wall', 'prateleira'] as const).includes(sp.get('tipo') as Kind) ? (sp.get('tipo') as Kind) : 'prova';
  const client = sp.get('para') === 'cliente';
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); n.set(k, v); setSp(n, { replace: true }); };
  if (!d) return <div className="rr-board"><p className="rr-faint" style={{ padding: 32 }}>carregando…</p></div>;
  const round = d.rounds.find((r) => r.id === sp.get('rodada')) ?? d.rounds.find((r) => r.status === 'open') ?? d.rounds[d.rounds.length - 1]!;
  const now = new Date();
  const stamp = `${now.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })} · ${now.toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })} (Brasília)`;
  const refs = round.items.filter((i) => i.kind === 'reference' && i.image);
  const proposals = round.items.filter((i) => i.kind === 'proposal' && i.status === 'ready' && i.image);
  const explorations = round.items.filter((i) => i.kind === 'exploration' && i.status === 'ready' && i.data.exploration);
  const pinned = d.rounds.flatMap((r) => r.items).filter((i) => i.pinned);

  const pages: { title: string; body: ReactNode }[] = [];
  if (kind === 'pedido') {
    const groups = chunk(refs, 4);
    groups.forEach((g, n) => pages.push({
      title: round.question,
      body: (
        <div className="rr-board__split">
          {n === 0 && <div className="rr-board__text">
            {d.question && <><span className="rr-board__label">o que a sala quer descobrir</span><p>{d.question}</p></>}
            <span className="rr-board__label">o que não muda</span><p>{round.constraints || 'nada foi fixado para esta rodada'}</p>
            {d.context && <><span className="rr-board__label">o que já vale</span><p className="rr-board__small">{d.context}</p></>}
          </div>}
          <div className="rr-board__grid" style={{ gridTemplateColumns: `repeat(${Math.max(1, Math.min(g.length, n === 0 ? 2 : 4))}, 1fr)` }}>
            {g.length === 0 ? <p className="rr-board__small">nenhuma referência enviada nesta rodada</p> : g.map((i) => <figure key={i.id} className="rr-board__fig"><div><img src={i.image!.url} alt="" /></div><figcaption><b>referência</b> {i.title}</figcaption></figure>)}
          </div>
        </div>
      ),
    }));
  } else if (kind === 'prateleira') {
    const item = d.rounds.flatMap((r) => r.items).find((i) => i.id === sp.get('item')) ?? d.rounds.flatMap((r) => r.items).filter((i) => i.board?.length).pop();
    const shots = (item?.board ?? []).filter((x) => x.image);
    const groups = chunk(shots, 8);
    const host = (u: string) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return u; } };
    groups.forEach((g, n) => pages.push({
      title: item?.data.ask ?? d.title,
      body: (
        <div className="rr-board__stack">
          <div className="rr-board__grid rr-board__grid--shelf">
            {g.length === 0 ? <p className="rr-board__small">nenhuma embalagem nesta prateleira</p> : g.map((x, i) => (
              <figure key={x.pageUrl} className="rr-board__fig rr-board__fig--shelf">
                <div><img src={x.image!.url} alt="" /></div>
                <figcaption><b>{String(n * 8 + i + 1).padStart(2, '0')} {x.brand}</b>{x.note}<span className="rr-board__tally">{host(x.pageUrl)}</span></figcaption>
              </figure>
            ))}
          </div>
          {n === groups.length - 1 && item?.body && <p className="rr-board__learned"><span className="rr-board__label">o que se repete e quem foge</span>{item.body}</p>}
        </div>
      ),
    }));
  } else if (kind === 'prova') {
    // cada exploração é uma página: a resposta, os caminhos lado a lado com o placar, o que falta e a pergunta seguinte
    for (const i of explorations) {
      const x = i.data.exploration!;
      pages.push({
        title: i.data.ask ?? round.question,
        body: (
          <div className="rr-board__stack">
            <p>{x.answer}</p>
            <div className="rr-board__grid" style={{ gridTemplateColumns: `repeat(${Math.max(1, x.paths.length)}, 1fr)` }}>
              {x.paths.map((p, k) => (
                <div key={k} className="rr-board__text">
                  <span className="rr-board__label">caminho {k + 1}</span>
                  <p><b>{p.title}</b><br />{p.thesis}</p>
                  {p.pros.length > 0 && <p className="rr-board__small"><b>a favor:</b> {p.pros.join(' · ')}</p>}
                  {p.risks.length > 0 && <p className="rr-board__small"><b>riscos:</b> {p.risks.join(' · ')}</p>}
                </div>
              ))}
            </div>
            {i.judgments.length > 0 && <p className="rr-board__small"><b>{i.title}:</b> {i.tally.keep} segue · {i.tally.maybe} talvez · {i.tally.drop} descarta{i.tally.split ? ' · divergência' : ''}{!client ? i.judgments.filter((j) => j.note).map((j) => ` · ${j.name.split(' ')[0]} (${LAB_VERDICT_LABELS[j.verdict]}): ${j.note}`).join('') : ''}</p>}
            {x.gaps.length > 0 && <p className="rr-board__small"><b>o que falta saber:</b> {x.gaps.map((g) => g.what).join(' · ')}</p>}
            {x.nextQuestion && <p className="rr-board__learned"><span className="rr-board__label">pergunta para a próxima rodada</span>{x.nextQuestion}</p>}
          </div>
        ),
      });
    }
    const groups = proposals.length || !explorations.length ? chunk(proposals, 4) : [];
    groups.forEach((g, n) => pages.push({
      title: round.question,
      body: (
        <div className="rr-board__stack">
          {n === 0 && round.constraints && <p className="rr-board__small"><b>não muda:</b> {round.constraints}</p>}
          <div className="rr-board__grid" style={{ gridTemplateColumns: `repeat(${Math.max(2, g.length)}, 1fr)` }}>
            {g.length === 0 ? <p className="rr-board__small">nenhuma proposta pronta nesta rodada</p> : g.map((i) => (
              <figure key={i.id} className="rr-board__fig">
                <div><img src={i.image!.url} alt="" /></div>
                <figcaption>
                  <b>{i.title}</b> {i.body}
                  {i.judgments.length > 0 && <span className="rr-board__tally">{i.tally.keep} segue · {i.tally.maybe} talvez · {i.tally.drop} descarta{i.tally.split ? ' · divergência' : ''}</span>}
                  {!client && i.judgments.filter((j) => j.note).map((j) => <span key={j.userId} className="rr-board__why"><b>{j.name.split(' ')[0]}</b> ({LAB_VERDICT_LABELS[j.verdict]}): <i>{j.note}</i></span>)}
                </figcaption>
              </figure>
            ))}
          </div>
          {n === groups.length - 1 && round.summary && <p className="rr-board__learned"><span className="rr-board__label">o que se aprendeu</span>{round.summary}</p>}
        </div>
      ),
    }));
  } else {
    // a wall como foi arranjada: a caixa que contém os cartões é reduzida para caber na página
    const W = 240, H = 380;
    const placed = pinned.map((i, n) => ({ i, x: i.wall?.x ?? 40 + (n % 5) * 280, y: i.wall?.y ?? 40 + Math.floor(n / 5) * 420 }));
    const minX = Math.min(...placed.map((p) => p.x)), minY = Math.min(...placed.map((p) => p.y));
    const maxX = Math.max(...placed.map((p) => p.x + W), 1), maxY = Math.max(...placed.map((p) => p.y + H), 1);
    pages.push({
      title: d.question || d.title,
      body: placed.length === 0 ? <p className="rr-board__small">nada foi marcado como interessante nesta sala</p> : (
        <div className="rr-board__wall" style={{ aspectRatio: `${maxX - minX} / ${maxY - minY}` }}>
          {placed.map(({ i, x, y }) => (
            <div key={i.id} className={`rr-board__pin${i.kind === 'note' ? ' is-note' : ''}`} style={{ left: `${((x - minX) / (maxX - minX)) * 100}%`, top: `${((y - minY) / (maxY - minY)) * 100}%`, width: `${(W / (maxX - minX)) * 100}%` }}>
              {i.image ? <img src={i.image.url} alt="" /> : <p>{(i.body ?? '').slice(0, 260)}</p>}
              {i.kind !== 'note' && <span>{i.kind === 'proposal' ? i.title : i.kind === 'reference' ? 'referência' : i.kind === 'exploration' ? `estudo ${i.title}` : 'pesquisa'}{i.wallNote ? <i> {i.wallNote}</i> : null}</span>}
            </div>
          ))}
        </div>
      ),
    });
  }

  return (
    <div className="rr-board">
      <div className="rr-board__bar">
        <Link to={`/lab/${id}`} className="rr-action rr-action--bare">← a sala</Link>
        <Segmented options={[{ value: 'pedido', label: 'solicitação' }, { value: 'prova', label: 'prova de conceito' }, { value: 'wall', label: 'wall' }]} value={kind} onChange={(v) => set('tipo', v)} />
        {kind !== 'wall' && kind !== 'prateleira' && d.rounds.length > 1 && <Segmented options={d.rounds.map((r) => ({ value: r.id, label: `rodada ${r.number}` }))} value={round.id} onChange={(v) => set('rodada', v)} />}
        {kind !== 'pedido' && kind !== 'prateleira' && <Segmented options={[{ value: 'raro', label: 'uso interno' }, { value: 'cliente', label: 'para o cliente' }]} value={client ? 'cliente' : 'raro'} onChange={(v) => set('para', v)} />}
        <Button variant="primary" size="sm" onClick={() => window.print()}>imprimir ou salvar em PDF</Button>
      </div>
      {pages.map((p, n) => (
        <section key={n} className="rr-board__page">
          <header className="rr-board__head">
            <MachineRaroBrand />
            <div className="rr-board__who">
              {d.project && <span><small>projeto</small>{d.project.name}</span>}
              <span><small>sala</small>{d.title}</span>
              <span><small>cliente</small><b>{d.organization.name}</b></span>
            </div>
            <span className="rr-board__eyebrow">prancha · {KIND[kind].eyebrow}{kind !== 'wall' && kind !== 'prateleira' ? ` · rodada ${round.number}` : ''}</span>
            <h1>{p.title}</h1>
          </header>
          <div className="rr-board__body">{p.body}</div>
          <footer className="rr-board__foot">
            <span>{stamp}<small>pedida por {me?.name ?? ''} · montada pela Machine</small></span>
            <i>{KIND[kind].note}</i>
            <span className="rr-board__right">{client && kind !== 'pedido' && kind !== 'prateleira' ? 'para o cliente' : 'uso interno'}<small>página {n + 1} de {pages.length}</small></span>
          </footer>
        </section>
      ))}
    </div>
  );
}
